import api from './api';

// All query functions resolve with the response body - they never reject.
// Two response shapes exist on the backend:
//  - /signin and /admin/me (utils/auth.ts, ResponseBuilder): { message, session } or { error }
//  - everything else under /admin (ApiResponse): { success, data, meta } or { success: false, error }
// Callers must inspect the resolved value accordingly.

const unwrap = (promise) =>
	promise.then((res) => res.data).catch((err) => err.response?.data || err);

// ── Auth ──

export const signin = (email, password) => unwrap(api.post('/signin', { email, password }));

export const getMe = () => unwrap(api.get('/admin/me'));

// Public self-service account endpoints (pi_ecommerce_api adminAccount.routes.ts).
// Normalized to { data } on success or { error } on failure, where `error` is
// always a display-safe string.
const unwrapAccount = (promise) =>
	promise
		.then((res) => ({ data: res.data?.data?.message }))
		.catch((err) => ({
			error: err.response?.data?.error?.message || 'Something went wrong. Please try again.',
		}));

export const register = ({ firstname, lastname, email, password }) =>
	unwrapAccount(api.post('/register', { firstname, lastname, email, password }));

export const verifyEmail = ({ email, token }) => unwrapAccount(api.post('/verify-email', { email, token }));

export const forgotPassword = (email) => unwrapAccount(api.post('/forgot-password', { email }));

export const resetPassword = ({ email, token, password }) =>
	unwrapAccount(api.post('/reset-password', { email, token, password }));


// ── Stores ──

export const listStores = (params = {}) => unwrap(api.get('/admin/stores', { params }));

// ── ERP lookups (store-creation wizard; no storeId yet, unscoped reference
// data + a customer-number lookup used to auto-populate store defaults) ──

export const lookupPaceCustomer = (customerNumber) =>
	unwrap(api.get(`/admin/pace-lookup/customers/${encodeURIComponent(customerNumber)}`));
export const listPaceCustomerPaymentMethods = (customerNumber) =>
	unwrap(api.get(`/admin/pace-lookup/customers/${encodeURIComponent(customerNumber)}/payment-methods`));
export const listPaceShipVia = () => unwrap(api.get('/admin/pace-lookup/ship-via'));
export const listPaceTerms = () => unwrap(api.get('/admin/pace-lookup/terms'));
export const listPaceCsrs = () => unwrap(api.get('/admin/pace-lookup/csrs'));
export const listPaceSalespersons = () => unwrap(api.get('/admin/pace-lookup/salespersons'));
export const listPaceJobTypes = () => unwrap(api.get('/admin/pace-lookup/job-types'));
export const listPaceManufacturingLocations = () =>
	unwrap(api.get('/admin/pace-lookup/manufacturing-locations'));
export const getStore = (storeId) => unwrap(api.get(`/admin/stores/${storeId}`));
export const createStore = (fields) => unwrap(api.post('/admin/stores', fields));
export const updateStore = (storeId, fields) => unwrap(api.patch(`/admin/stores/${storeId}`, fields));
export const checkOrderNumberPrefix = (storeId, prefix) =>
	unwrap(api.get(`/admin/stores/${storeId}/order-number-prefix-availability`, { params: { prefix } }));
export const createStorePreviewSession = (storeId, fields) =>
	unwrap(api.post(`/admin/stores/${storeId}/preview-session`, fields));
// Iframe stores (storefront_type 'iframe'): non-secret launch config, the JWT
// secret (write-only -- never returned), and the "View Store" test page's
// server-signed launch URL.
export const getIframeConfig = (storeId) => unwrap(api.get(`/admin/stores/${storeId}/iframe-config`));
export const updateIframeConfig = (storeId, config) =>
	unwrap(api.put(`/admin/stores/${storeId}/iframe-config`, config));
export const setIframeSecret = (storeId, body) => unwrap(api.put(`/admin/stores/${storeId}/iframe-secret`, body));
export const clearIframeSecret = (storeId) => unwrap(api.delete(`/admin/stores/${storeId}/iframe-secret`));
export const createIframePreviewSession = (storeId, values) =>
	unwrap(api.post(`/admin/stores/${storeId}/iframe-preview`, { values }));

export const getStoreDefaults = (storeId) => unwrap(api.get(`/admin/stores/${storeId}/defaults`));
export const saveStoreDefaults = (storeId, fields) =>
	unwrap(api.put(`/admin/stores/${storeId}/defaults`, fields));

export const getStoreEmailSettings = (storeId) => unwrap(api.get(`/admin/stores/${storeId}/email-settings`));
export const saveStoreEmailSettings = (storeId, fields) =>
	unwrap(api.put(`/admin/stores/${storeId}/email-settings`, fields));
// Renders one customer email with sample data, using the store's custom
// template when useCustomEmailTemplates is on (else the default). -> { template, source, subject, html }
export const previewStoreEmailTemplate = (storeId, templateKey) =>
	unwrap(api.get(`/admin/stores/${storeId}/email-templates/${templateKey}/preview`));

export const getStorePaceAccount = (storeId) => unwrap(api.get(`/admin/stores/${storeId}/pace-account`));
export const saveStorePaceAccount = (storeId, fields) =>
	unwrap(api.put(`/admin/stores/${storeId}/pace-account`, fields));

export const listLoyaltyPrograms = () => unwrap(api.get('/admin/loyalty-programs'));
export const listStoreLoyaltyPrograms = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/loyalty-programs`));
export const addStoreLoyaltyProgram = (storeId, loyaltyProgramId) =>
	unwrap(api.post(`/admin/stores/${storeId}/loyalty-programs`, { loyaltyProgramId }));
export const removeStoreLoyaltyProgram = (id) =>
	unwrap(api.delete(`/admin/stores/loyalty-programs/${id}`));

export const listStorePaymentMethods = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/payment-methods`));
export const addStorePaymentMethod = (storeId, paymentMethod) =>
	unwrap(api.post(`/admin/stores/${storeId}/payment-methods`, { paymentMethod }));
export const removeStorePaymentMethod = (id) =>
	unwrap(api.delete(`/admin/stores/payment-methods/${id}`));

export const listStoreCustomerMappings = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/customer-mappings`));
export const addStoreCustomerMapping = (storeId, fields) =>
	unwrap(api.post(`/admin/stores/${storeId}/customer-mappings`, fields));
export const updateStoreCustomerMapping = (id, fields) =>
	unwrap(api.patch(`/admin/stores/customer-mappings/${id}`, fields));
export const removeStoreCustomerMapping = (id) =>
	unwrap(api.delete(`/admin/stores/customer-mappings/${id}`));

export const listShippingMethodMappings = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/shipping-method-mappings`));
export const addShippingMethodMapping = (storeId, fields) =>
	unwrap(api.post(`/admin/stores/${storeId}/shipping-method-mappings`, fields));
export const updateShippingMethodMapping = (id, fields) =>
	unwrap(api.patch(`/admin/stores/shipping-method-mappings/${id}`, fields));
export const removeShippingMethodMapping = (id) =>
	unwrap(api.delete(`/admin/stores/shipping-method-mappings/${id}`));
export const listShipViaOptions = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/ship-via-options`));

export const triggerStoreProductSync = (storeId) =>
	unwrap(api.post(`/admin/stores/${storeId}/product-sync`));

export const getStoreProductSyncStatus = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/product-sync-status`));

export const listPaymentTermMappings = (storeId) =>
	unwrap(api.get(`/admin/stores/${storeId}/payment-term-mappings`));
export const addPaymentTermMapping = (storeId, fields) =>
	unwrap(api.post(`/admin/stores/${storeId}/payment-term-mappings`, fields));
export const updatePaymentTermMapping = (id, fields) =>
	unwrap(api.patch(`/admin/stores/payment-term-mappings/${id}`, fields));
export const removePaymentTermMapping = (id) =>
	unwrap(api.delete(`/admin/stores/payment-term-mappings/${id}`));

export const listStoreApiTokens = (storeId) => unwrap(api.get(`/admin/stores/${storeId}/api-tokens`));
export const issueStoreApiToken = (storeId, tokenName) =>
	unwrap(api.post(`/admin/stores/${storeId}/api-tokens`, { tokenName }));
export const revokeStoreApiToken = (id) => unwrap(api.delete(`/admin/stores/api-tokens/${id}`));

// ── Orders ──

export const listOrders = (params = {}) =>
	unwrap(api.get('/admin/orders', { params }));
export const getOrder = (orderId) => unwrap(api.get(`/admin/orders/${orderId}`));
export const getOrderStatus = (orderId) => unwrap(api.get(`/admin/orders/${orderId}/status`));
export const replayOrder = (orderId, reason, validateOnly = false) =>
	unwrap(api.post(`/admin/orders/${orderId}/replay`, { reason, validate_only: validateOnly }));
export const submitOrderToPace = (orderId, mode, force = false) =>
	unwrap(api.post(`/admin/orders/${orderId}/submit-to-pace`, { mode, force }));
export const cancelOrder = (orderId, reason) =>
	unwrap(api.post(`/admin/orders/${orderId}/cancel`, { reason }));
export const simulateJobStatus = (orderId, paceJobStatus, reason) =>
	unwrap(api.post(`/admin/orders/${orderId}/simulate-job-status`, { paceJobStatus, reason }));
// anticipatedShipDate is a plain 'YYYY-MM-DD' calendar day. Store ADMIN /
// PLATFORMADMIN only; also moves the Pace job's Promise/Scheduled Ship Date
// when the order is already in Pace (response pace_sync: updated|failed|not_submitted).
export const updateOrderShipDate = (orderId, anticipatedShipDate) =>
	unwrap(api.patch(`/admin/orders/${orderId}/anticipated-ship-date`, { anticipatedShipDate }));
// { orderIds, action: 'approve'|'release'|'cancel'|'set_ship_date', reason?, anticipatedShipDate? }
// -> { results: [{ order_id, status: ok|skipped|failed, message }], summary }.
// Ineligible orders come back 'skipped' rather than failing the batch.
export const bulkOrderAction = (body) => unwrap(api.post('/admin/orders/bulk', body));

// ── Order Approvals ──
// Orders sitting in PENDING_APPROVAL for the active store, gated by
// stores.require_order_approval / user_store_access.is_order_approver -- see
// orderApprovalsAdmin.controller.ts / adminOrders.controller.ts's
// approveOrder/rejectOrder (modeled on cancelOrder above).

export const listPendingApprovals = (params = {}) =>
	unwrap(api.get('/admin/approvals', { params }));
// The approve endpoint takes no body (no reason/note) -- see
// adminOrders.controller.ts#approveOrder.
export const approveOrder = (orderId) => unwrap(api.post(`/admin/orders/${orderId}/approve`));
export const rejectOrder = (orderId, reason) =>
	unwrap(api.post(`/admin/orders/${orderId}/reject`, { reason }));

// ── Order Reviews ──
// Orders sitting in PENDING_REVIEW for the active store, gated by
// stores.require_order_review -- see orderReviewsAdmin.controller.ts /
// adminOrders.controller.ts#releaseOrder. Any store admin may release; there
// is no review-specific reject (use cancelOrder).

export const listPendingReviews = (params = {}) => unwrap(api.get('/admin/reviews', { params }));
// Moves the order to READY_FOR_ERP for the next ERP submission run. No body.
export const releaseOrder = (orderId) => unwrap(api.post(`/admin/orders/${orderId}/release`));

// ── Fulfillments ──

export const listFulfillments = (params = {}) =>
	unwrap(api.get('/admin/fulfillments', { params }));
export const getFulfillment = (id) => unwrap(api.get(`/admin/fulfillments/${id}`));

// ── Products ──

// Uploads a product/logo image to pi_ecommerce_api's local /uploads store
// (see uploads.controller.ts) and returns { success, data: { url } }.
export const uploadFile = ({ file, storeId, kind, productId }) => {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('storeId', storeId);
	formData.append('kind', kind);
	if (productId) formData.append('productId', productId);
	// Let axios/the browser set the multipart Content-Type + boundary itself --
	// the api instance's default 'application/json' header would otherwise win.
	return unwrap(api.post('/admin/uploads', formData, { headers: { 'Content-Type': undefined } }));
};

export const listProducts = (params = {}) => unwrap(api.get('/admin/products', { params }));
export const getProduct = (productId) => unwrap(api.get(`/admin/products/${productId}`));
export const createProduct = (fields) => unwrap(api.post('/admin/products', fields));
export const updateProduct = (productId, fields) => unwrap(api.patch(`/admin/products/${productId}`, fields));

// ERP inventory items detected with no matching product yet (see
// erp_products) -- awaiting a PLATFORMADMIN/ADMIN to review and publish.
export const publishErpProduct = (id) => unwrap(api.post(`/admin/erp-products/${id}/publish`));

export const listCategories = (params = {}) => unwrap(api.get('/admin/products/categories', { params }));
export const createCategory = (fields) => unwrap(api.post('/admin/products/categories', fields));
export const updateCategory = (id, fields) => unwrap(api.patch(`/admin/products/categories/${id}`, fields));
export const removeCategory = (id) => unwrap(api.delete(`/admin/products/categories/${id}`));

export const listFulfillmentRouting = (params = {}) =>
	unwrap(api.get('/admin/products/fulfillment-routing', { params }));
export const createFulfillmentRouting = (fields) => unwrap(api.post('/admin/products/fulfillment-routing', fields));
export const updateFulfillmentRouting = (id, fields) =>
	unwrap(api.patch(`/admin/products/fulfillment-routing/${id}`, fields));
export const removeFulfillmentRouting = (id) => unwrap(api.delete(`/admin/products/fulfillment-routing/${id}`));

export const listProductOptions = (productId) => unwrap(api.get(`/admin/products/${productId}/options`));
export const createOption = (productId, fields) => unwrap(api.post(`/admin/products/${productId}/options`, fields));
export const updateOption = (id, fields) => unwrap(api.patch(`/admin/products/options/${id}`, fields));
export const removeOption = (id) => unwrap(api.delete(`/admin/products/options/${id}`));

export const createOptionValue = (optionId, fields) =>
	unwrap(api.post(`/admin/products/options/${optionId}/values`, fields));
export const updateOptionValue = (id, fields) => unwrap(api.patch(`/admin/products/option-values/${id}`, fields));
export const removeOptionValue = (id) => unwrap(api.delete(`/admin/products/option-values/${id}`));

export const listProductVariants = (productId) => unwrap(api.get(`/admin/products/${productId}/variants`));
export const createVariant = (productId, fields) => unwrap(api.post(`/admin/products/${productId}/variants`, fields));
export const updateVariant = (id, fields) => unwrap(api.patch(`/admin/products/variants/${id}`, fields));
export const removeVariant = (id) => unwrap(api.delete(`/admin/products/variants/${id}`));

export const listKits = (params = {}) => unwrap(api.get('/admin/products/kits', { params }));
export const listKitItems = (kitProductId) => unwrap(api.get(`/admin/products/${kitProductId}/kit-items`));
export const createKitItem = (kitProductId, fields) =>
	unwrap(api.post(`/admin/products/${kitProductId}/kit-items`, fields));
export const updateKitItem = (id, fields) => unwrap(api.patch(`/admin/products/kit-items/${id}`, fields));
export const removeKitItem = (id) => unwrap(api.delete(`/admin/products/kit-items/${id}`));
export const reorderKitItems = (kitProductId, orderedIds) =>
	unwrap(api.patch(`/admin/products/${kitProductId}/kit-items/reorder`, { orderedIds }));

export const listProductImages = (productId) => unwrap(api.get(`/admin/products/${productId}/images`));
export const createImage = (productId, fields) => unwrap(api.post(`/admin/products/${productId}/images`, fields));
export const updateImage = (id, fields) => unwrap(api.patch(`/admin/products/images/${id}`, fields));
export const removeImage = (id) => unwrap(api.delete(`/admin/products/images/${id}`));

export const listProductPricingTiers = (productId) => unwrap(api.get(`/admin/products/${productId}/pricing-tiers`));
export const createPricingTier = (productId, fields) =>
	unwrap(api.post(`/admin/products/${productId}/pricing-tiers`, fields));
export const updatePricingTier = (id, fields) => unwrap(api.patch(`/admin/products/pricing-tiers/${id}`, fields));
export const removePricingTier = (id) => unwrap(api.delete(`/admin/products/pricing-tiers/${id}`));

export const listProductErpMappings = (params = {}) =>
	unwrap(api.get('/admin/products/erp-mappings', { params }));

// ── FAQ ──

export const listFaq = (storeId) => unwrap(api.get('/admin/faqs', { params: { storeId } }));
export const listFaqBank = () => unwrap(api.get('/admin/faqs/bank'));
export const publishFaq = (storeId) => unwrap(api.post('/admin/faqs/publish', { storeId }));

export const createFaqCategory = (fields) => unwrap(api.post('/admin/faqs/categories', fields));
export const updateFaqCategory = (id, fields) => unwrap(api.patch(`/admin/faqs/categories/${id}`, fields));
export const removeFaqCategory = (id) => unwrap(api.delete(`/admin/faqs/categories/${id}`));
export const reorderFaqCategories = (storeId, orderedIds) =>
	unwrap(api.post('/admin/faqs/categories/reorder', { storeId, orderedIds }));

export const createFaqItem = (fields) => unwrap(api.post('/admin/faqs/items', fields));
export const updateFaqItem = (id, fields) => unwrap(api.patch(`/admin/faqs/items/${id}`, fields));
export const removeFaqItem = (id) => unwrap(api.delete(`/admin/faqs/items/${id}`));
export const reorderFaqItems = (categoryId, orderedIds) =>
	unwrap(api.post('/admin/faqs/items/reorder', { categoryId, orderedIds }));
export const addFaqItemFromBank = (bankItemId, storeId) =>
	unwrap(api.post('/admin/faqs/items/from-bank', { bankItemId, storeId }));

// ── Coupons ──

export const listCoupons = (params = {}) => unwrap(api.get('/admin/coupons', { params }));
export const getCoupon = (couponId) => unwrap(api.get(`/admin/coupons/${couponId}`));
export const createCoupon = (fields) => unwrap(api.post('/admin/coupons', fields));
export const updateCoupon = (couponId, fields) => unwrap(api.patch(`/admin/coupons/${couponId}`, fields));
export const removeCoupon = (couponId) => unwrap(api.delete(`/admin/coupons/${couponId}`));

// ── Inventory (view-only) ──

export const listInventoryLevels = (params = {}) => unwrap(api.get('/admin/inventory', { params }));
export const listInventoryChangeLog = (params = {}) => unwrap(api.get('/admin/inventory/change-log', { params }));

// ── Customers (view) ──

export const listCustomers = (params = {}) => unwrap(api.get('/admin/customers', { params }));
export const getCustomer = (id) => unwrap(api.get(`/admin/customers/${id}`));

// ── Store Users (view) ──

export const listStoreAccounts = (params = {}) => unwrap(api.get('/admin/store-accounts', { params }));
export const getStoreAccount = (id) => unwrap(api.get(`/admin/store-accounts/${id}`));

// ── Reports ──

export const getOverviewReport = (params = {}) => unwrap(api.get('/admin/reports/overview', { params }));
export const getCommandCenterSummary = (params = {}) => unwrap(api.get('/admin/reports/command-center', { params }));
export const getFulfillmentsReport = (params = {}) => unwrap(api.get('/admin/reports/fulfillments', { params }));
export const getInventoryReport = (params = {}) => unwrap(api.get('/admin/reports/inventory', { params }));
export const getLoyaltyReport = (params = {}) => unwrap(api.get('/admin/reports/loyalty', { params }));
export const getCouponUsageReport = (params = {}) => unwrap(api.get('/admin/reports/coupon-usage', { params }));
export const getSyncHealthReport = (params = {}) => unwrap(api.get('/admin/reports/sync-health', { params }));
export const getWebhookDeliveriesReport = (params = {}) => unwrap(api.get('/admin/reports/webhook-deliveries', { params }));
export const getCustomFulfillmentReport = (params = {}) => unwrap(api.get('/admin/reports/custom-fulfillment', { params }));
export const getCustomFulfillmentArtifact = (integrationEventId, params = {}) =>
	unwrap(api.get(`/admin/reports/custom-fulfillment/${integrationEventId}/artifact`, { params }));
export const retryWebhookDelivery = (id) => unwrap(api.post(`/admin/webhooks/deliveries/${id}/retry`));

// ── Users & Permissions ──

export const listUsers = (params = {}) => unwrap(api.get('/admin/users', { params }));
export const createUser = (fields) => unwrap(api.post('/admin/users', fields));
export const updateUser = (id, fields) => unwrap(api.patch(`/admin/users/${id}`, fields));
export const listUserStoreAccess = (id) => unwrap(api.get(`/admin/users/${id}/store-access`));
export const grantUserStoreAccess = (id, storeId, role) =>
	unwrap(api.post(`/admin/users/${id}/store-access`, { storeId, role }));
export const updateUserStoreAccessRole = (accessId, role) =>
	unwrap(api.patch(`/admin/users/store-access/${accessId}`, { role }));
export const revokeUserStoreAccess = (accessId) => unwrap(api.delete(`/admin/users/store-access/${accessId}`));

// Per-store role map for the logged-in user, e.g. [{storeId, role}] -- role
// is per-store now, so nav gating needs this instead of a single flat
// userProfile.role (which only ever means PLATFORMADMIN-or-not going forward).
export const getMyStoreRoles = () => unwrap(api.get('/admin/me/store-roles'));

// ── Store-scoped team management (ADMIN role, excludes PLATFORMADMIN) ──
// Scoped server-side to the caller's assigned store(s) -- see
// storeUsers.service.ts. Distinct from the PLATFORMADMIN-only /admin/users
// endpoints above, which manage every store's staff unrestricted.

export const listStoreUsers = (params = {}) => unwrap(api.get('/admin/store-users', { params }));
export const updateStoreUserRole = (id, storeId, role) =>
	unwrap(api.patch(`/admin/store-users/${id}`, { storeId, role }));
// Same PATCH endpoint as updateStoreUserRole above (storeUsers.routes.ts only
// has the one update route) -- role is required server-side today
// (storeUsers.controller.ts#updateRole 422s without it), so this always
// re-sends the row's current role alongside isOrderApprover. NOTE: as of this
// writing the backend controller/service only reads `role` off this body and
// neither persists nor returns `isOrderApprover` (see storeUsers.service.ts's
// updateStoreUserRole and listStoreUsers' `attributes` allowlist) -- this
// call is wired for the intended contract but is a no-op until that lands.
export const updateStoreUserApprover = (userId, storeId, isOrderApprover, role) =>
	unwrap(api.patch(`/admin/store-users/${userId}`, { storeId, role, isOrderApprover }));
export const revokeStoreUserAccess = (accessId) => unwrap(api.delete(`/admin/store-users/store-access/${accessId}`));
export const inviteStoreUser = (fields) => unwrap(api.post('/admin/store-users/invite', fields));
export const resendStoreUserInvite = (userId, storeId) =>
	unwrap(api.post(`/admin/store-users/${userId}/resend-invite`, { storeId }));

// Public -- no auth token, the invite token itself is the credential.
export const acceptUserInvite = (token, password) => unwrap(api.post('/invites/accept', { token, password }));

// ── Webhooks & Integrations ──

export const listWebhookSubscriptions = (params = {}) => unwrap(api.get('/admin/webhooks', { params }));
export const createWebhookSubscription = (fields) => unwrap(api.post('/admin/webhooks', fields));
export const updateWebhookSubscription = (id, fields) => unwrap(api.patch(`/admin/webhooks/${id}`, fields));
export const removeWebhookSubscription = (id) => unwrap(api.delete(`/admin/webhooks/${id}`));
export const listWebhookEventTypes = () => unwrap(api.get('/admin/webhooks/event-types'));
export const listWebhookEntityTypes = () => unwrap(api.get('/admin/webhooks/entity-types'));

// ── Notification Rules ──

export const listNotificationRules = (params = {}) => unwrap(api.get('/admin/notification-rules', { params }));
export const createNotificationRule = (fields) => unwrap(api.post('/admin/notification-rules', fields));
export const updateNotificationRule = (id, fields) => unwrap(api.patch(`/admin/notification-rules/${id}`, fields));
export const removeNotificationRule = (id) => unwrap(api.delete(`/admin/notification-rules/${id}`));
export const listNotificationRuleEventTypes = () => unwrap(api.get('/admin/notification-rules/event-types'));
export const listNotificationHistory = (params = {}) => unwrap(api.get('/admin/notification-rules/history', { params }));

// ── Audit Log ──

export const listAuditLog = (params = {}) => unwrap(api.get('/admin/audit-log', { params }));
export const listCommerceEvents = (params = {}) => unwrap(api.get('/admin/events', { params }));
export const listAttentionQueueItems = (params = {}) => unwrap(api.get('/admin/attention-queue', { params }));
export const dismissAttentionQueueItem = (id, storeId, reason) =>
	unwrap(api.post(`/admin/attention-queue/${id}/dismiss`, { storeId, reason }));

// ── Workflows ──

export const listWorkflows = (params = {}) => unwrap(api.get('/admin/workflows', { params }));
export const getWorkflow = (id, params = {}) => unwrap(api.get(`/admin/workflows/${id}`, { params }));
export const createWorkflow = (fields) => unwrap(api.post('/admin/workflows', fields));
export const updateWorkflow = (id, fields) => unwrap(api.patch(`/admin/workflows/${id}`, fields));
export const createWorkflowDraft = (id, storeId) => unwrap(api.post(`/admin/workflows/${id}/draft`, { storeId }));
export const publishWorkflow = (id, storeId) => unwrap(api.post(`/admin/workflows/${id}/publish`, { storeId }));
export const pauseWorkflow = (id, storeId) => unwrap(api.post(`/admin/workflows/${id}/pause`, { storeId }));
export const resumeWorkflow = (id, storeId) => unwrap(api.post(`/admin/workflows/${id}/resume`, { storeId }));
export const archiveWorkflow = (id, storeId) => unwrap(api.post(`/admin/workflows/${id}/archive`, { storeId }));

export const listWorkflowRuns = (params = {}) => unwrap(api.get('/admin/workflow-runs', { params }));
export const getWorkflowRun = (id, params = {}) => unwrap(api.get(`/admin/workflow-runs/${id}`, { params }));
export const retryWorkflowRun = (id, storeId) => unwrap(api.post(`/admin/workflow-runs/${id}/retry`, { storeId }));
export const resumeWorkflowRun = (id, storeId) => unwrap(api.post(`/admin/workflow-runs/${id}/resume`, { storeId }));
export const cancelWorkflowRun = (id, storeId) => unwrap(api.post(`/admin/workflow-runs/${id}/cancel`, { storeId }));

export const listWorkflowApprovals = (params = {}) => unwrap(api.get('/admin/workflow-approvals', { params }));
export const approveWorkflowApproval = (id, storeId, comments) => unwrap(api.post(`/admin/workflow-approvals/${id}/approve`, { storeId, comments }));
export const rejectWorkflowApproval = (id, storeId, comments) => unwrap(api.post(`/admin/workflow-approvals/${id}/reject`, { storeId, comments }));

export const listWorkflowExceptions = (params = {}) => unwrap(api.get('/admin/workflow-exceptions', { params }));
export const retryWorkflowException = (id, storeId) => unwrap(api.post(`/admin/workflow-exceptions/${id}/retry`, { storeId }));
export const skipWorkflowException = (id, storeId) => unwrap(api.post(`/admin/workflow-exceptions/${id}/skip`, { storeId }));
export const cancelWorkflowException = (id, storeId) => unwrap(api.post(`/admin/workflow-exceptions/${id}/cancel`, { storeId }));

// ── Holidays ──
// One global non-shipping-day list used to calculate anticipated ship
// dates. PLATFORMADMIN only.

export const listHolidays = () => unwrap(api.get('/admin/holidays'));
export const createHoliday = (body) => unwrap(api.post('/admin/holidays', body));
export const updateHoliday = (id, body) => unwrap(api.patch(`/admin/holidays/${id}`, body));
export const deleteHoliday = (id) => unwrap(api.delete(`/admin/holidays/${id}`));
